// Dialog (009 revision 6, FR-044, FR-048; shadcn/ui `dialog`, restyled): a modal window centred over a scrim, on
// Radix Dialog: focus moves into it and stays there, Escape or the close button closes it and focus returns to the
// trigger, the page behind does not scroll, and the title names it. It scrolls inside itself when taller than the
// window (`data-ui-scroll="dialog"`). The reader app shows a bottom `Sheet` below 768 px and a dialog from 768 px.
// 010 revision 5: the panel is marked `aria-modal`, so the page behind it counts as inert.
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { CloseIcon } from '../../icons';
import { cn } from '../../lib/cn';

/**
 * The dialog's root (open state).
 * @param props Radix Dialog root props
 * @returns the root
 */
export function Dialog(props: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

/**
 * The button that opens the dialog (use `asChild` with a {@link Button}).
 * @param props Radix Dialog trigger props
 * @returns the trigger
 */
export function DialogTrigger(props: ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

/**
 * A control that closes the dialog (use `asChild` with a {@link Button}).
 * @param props Radix Dialog close props
 * @returns the control
 */
export function DialogClose(props: ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

/**
 * The scrim behind a dialog or sheet.
 * @param props Radix Dialog overlay props
 * @returns the scrim
 */
export function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn('fixed inset-0 z-50 bg-scrim', className)}
      {...props}
    />
  );
}

/** Props of {@link DialogContent}. */
export type DialogContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  /** Show the close button at the top end (default true). */
  showCloseButton?: boolean;
  /** The close button's accessible name (default "Close"; pass the translation). */
  closeLabel?: string;
  /** Where the dialog is rendered (default: the body). */
  container?: HTMLElement | null;
};

/**
 * The dialog's window, with the scrim and the close button.
 * @param props the close button, container and Radix Dialog content props
 * @returns the window
 */
export function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeLabel = 'Close',
  container,
  ...props
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal container={container}>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        aria-modal="true"
        data-ui-scroll="dialog"
        className={cn(
          'fixed inset-x-4 top-1/2 z-50 mx-auto flex max-h-dialog w-auto max-w-lg -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-xl border border-line bg-surface p-4 text-ink shadow-pop',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            aria-label={closeLabel}
            className="absolute end-2 top-2 inline-grid size-control-touch cursor-pointer place-items-center rounded-lg text-ink-secondary hover:bg-subtle hover:text-ink lg:size-control-sm"
          >
            <CloseIcon size={16} />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/**
 * The dialog's header: title and description (kept clear of the close button).
 * @param props the div's attributes
 * @returns the header
 */
export function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      data-ui-density="slot"
      className={cn('flex flex-col gap-2 pe-close text-start', className)}
      {...props}
    />
  );
}

/**
 * The dialog's footer: its actions, stacked on phones and in a row at the end from 768 px.
 * @param props the div's attributes
 * @returns the footer
 */
export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      data-ui-part="group"
      className={cn('flex flex-col-reverse gap-2 md:flex-row md:justify-end', className)}
      {...props}
    />
  );
}

/**
 * The dialog's title (its accessible name).
 * @param props Radix Dialog title props
 * @returns the title
 */
export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-display text-lg font-semibold wrap-anywhere', className)}
      {...props}
    />
  );
}

/**
 * The dialog's description (its accessible description).
 * @param props Radix Dialog description props
 * @returns the description
 */
export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-ink-secondary wrap-anywhere', className)}
      {...props}
    />
  );
}
